/**
 * Nội dung trang chủ công khai (Public Landing Page).
 *
 * Chỉnh sửa file này khi legal team gửi nội dung cuối cùng — KHÔNG cần thay đổi code.
 * Tham chiếu style: marketingarena.vn (dark tech, eyebrow brackets, stats, timeline).
 */

export interface SectionHighlight {
  title: string
  description: string
}

export interface RuleArticle {
  title: string
  body: string
}

export interface AwardItem {
  rank: string
  amount: string
  description?: string
}

export interface StatItem {
  value: string
  label: string
}

export interface TimelinePhase {
  code: string // "R1", "R2", "R3"
  title: string
  date: string // placeholder ngày tháng
  description: string
}

export interface PartnerLogo {
  name: string
  url?: string
}

export interface ContactCategory {
  category: string
  items: { label: string; value: string }[]
}

export const siteContent = {
  brand: {
    short: 'Gender Justice Moot Court',
    season: 'SEASON 2026',
    edition: 'PHIÊN BẢN 01',
  },

  // 2026-05-25: link Google Form đăng ký đội (BTC cấp acc thủ công sau khi
  // đội trưởng submit). TODO: legal team gửi link chính thức → update.
  // Trong khi chờ → empty string sẽ disable button + show tooltip cảnh báo.
  registrationFormUrl: '',

  hero: {
    eyebrow: '[SYS.RDY // EDITION.01]',
    title: 'CUỘC THI PHIÊN TOÀ GIẢ ĐỊNH VỀ CÔNG LÝ GIỚI',
    subtitle: 'Gender Justice Moot Court · UEL 2026',
    tagline:
      'Sân chơi học thuật pháp lý dành cho sinh viên — nơi tranh tụng trở thành công cụ kiến tạo bình đẳng giới.',
    ctaPrimary: 'ĐĂNG NHẬP',
    ctaSecondary: 'Tìm hiểu thêm',
  },

  stats: [
    { value: '32+', label: 'Đội thi đăng ký' },
    { value: '15+', label: 'Trường đại học' },
    { value: '20+', label: 'Giám khảo chuyên môn' },
    { value: '03', label: 'Vòng thi trực tuyến' },
  ] as StatItem[],

  overview: {
    eyebrow: '[ABOUT // 01]',
    title: 'Về Cuộc thi',
    description:
      'Cuộc thi Phiên toà Giả định về Công lý Giới là chương trình học thuật do Trường Đại học Kinh tế - Luật (UEL) tổ chức nhằm tạo môi trường thực hành tranh tụng, nghiên cứu pháp luật và lan toả tinh thần bình đẳng giới trong giới sinh viên luật khu vực phía Nam.',
    highlights: [
      {
        title: 'Học thuật chuyên sâu',
        description:
          'Tiếp cận hồ sơ vụ việc thực tế, hướng dẫn bởi Ban Giám khảo là chuyên gia pháp lý đầu ngành.',
      },
      {
        title: 'Tranh tụng trực tuyến',
        description:
          'Toàn bộ vòng thi tổ chức trên Nền tảng Gender Justice Moot Court — giao diện chuyên nghiệp, mô phỏng phiên toà thực tế.',
      },
      {
        title: 'Công lý giới',
        description:
          'Mỗi vụ việc xoay quanh các vấn đề bình đẳng giới — hôn nhân, lao động, tài sản, quyền nuôi con.',
      },
      {
        title: 'Cộng đồng học thuật',
        description:
          'Kết nối hàng trăm sinh viên luật, luật sư, giảng viên và nghiên cứu sinh giàu kinh nghiệm.',
      },
    ] as SectionHighlight[],
  },

  timeline: {
    eyebrow: '[TIMELINE // SEASON.01]',
    title: 'Hành trình 03 Vòng thi',
    subtitle: 'Toàn bộ vòng đấu được tổ chức trực tuyến trên nền tảng Gender Justice Moot Court.',
    phases: [
      {
        code: 'R1',
        title: 'Vòng Sơ loại',
        date: 'Sẽ được Ban Tổ chức công bố',
        description:
          'Khởi kiện và Giao nộp Chứng cứ trực tuyến. Đội thi nghiên cứu Bộ đề, soạn Đơn khởi kiện, lựa chọn chứng cứ và upload Video clip giới thiệu đội.',
      },
      {
        code: 'R2',
        title: 'Vòng Bán kết',
        date: 'Sẽ được Ban Tổ chức công bố',
        description:
          'Phiên kiểm tra Chứng cứ và Hoà giải trực tuyến. 04 đội xuất sắc nhất từ Vòng Sơ loại được phân vai và tham gia phiên họp trực tuyến với Thẩm phán.',
      },
      {
        code: 'R3',
        title: 'Vòng Chung kết',
        date: 'Sẽ được Ban Tổ chức công bố',
        description:
          'Phiên Tranh tụng trực tiếp tại UEL. 02 đội cuối cùng đối mặt trên phiên toà thật — Bản án được công bố công khai sau cuộc thi.',
      },
    ] as TimelinePhase[],
  },

  rules: {
    eyebrow: '[RULES // 02]',
    title: 'Thể lệ Cuộc thi',
    subtitle:
      'Các quy định cốt lõi — chi tiết đầy đủ sẽ được Ban Tổ chức cung cấp tới các đội thi sau khi đăng ký.',
    articles: [
      {
        title: 'Đối tượng dự thi',
        body: 'Sinh viên đang theo học các trường đại học có đào tạo chuyên ngành luật trên toàn quốc. Mỗi đội gồm đúng 04 thành viên do Đội trưởng đại diện đăng ký.',
      },
      {
        title: 'Cơ cấu vòng thi',
        body: 'Cuộc thi gồm 03 vòng: Vòng Sơ loại (khởi kiện và giao nộp chứng cứ trực tuyến), Vòng Bán kết (phiên kiểm tra chứng cứ - hoà giải trực tuyến), Vòng Chung kết (tranh tụng trực tiếp).',
      },
      {
        title: 'Tiêu chí đánh giá',
        body: 'Tổng điểm 100 trên 05 tiêu chí: tư duy pháp lý, tư duy bình đẳng giới, kỹ năng soạn thảo văn bản tố tụng, khả năng nhận diện - đánh giá chứng cứ, và kỹ năng lập luận trong hồ sơ vụ việc.',
      },
      {
        title: 'Sản phẩm bài thi',
        body: 'Mỗi đội nộp 03 thành phần: Đơn khởi kiện, Danh mục chứng cứ chọn lọc từ Bộ đề thi, và Video clip giới thiệu đội (≤ 10 phút).',
      },
      {
        title: 'Quy định công khai kết quả',
        body: 'Kết quả các vòng được công bố trên trang chủ. Bản án Vòng Chung kết được công khai sau khi Ban Tổ chức phê duyệt.',
      },
    ] as RuleArticle[],
  },

  awards: {
    eyebrow: '[AWARDS // 03]',
    title: 'Giải thưởng',
    subtitle:
      'Tổng giá trị giải thưởng và các hình thức ghi nhận dành cho đội thi xuất sắc — chi tiết sẽ được công bố trong Lễ trao giải.',
    items: [
      {
        rank: 'Giải Nhất',
        amount: 'Sẽ được Ban Tổ chức công bố',
        description:
          'Cúp lưu niệm + Bằng khen + Tiền thưởng và cơ hội thực tập tại các văn phòng luật đối tác.',
      },
      {
        rank: 'Giải Nhì',
        amount: 'Sẽ được Ban Tổ chức công bố',
        description: 'Bằng khen + Tiền thưởng và quà tặng từ các nhà tài trợ.',
      },
      {
        rank: 'Giải Ba',
        amount: 'Sẽ được Ban Tổ chức công bố',
        description: 'Bằng khen + Tiền thưởng và quà tặng từ các nhà tài trợ.',
      },
      {
        rank: 'Giải Phong cách',
        amount: 'Sẽ được Ban Tổ chức công bố',
        description: 'Dành cho đội có Video clip giới thiệu xuất sắc nhất theo bình chọn của Ban Giám khảo.',
      },
    ] as AwardItem[],
  },

  partners: {
    eyebrow: '[PARTNERS // 04]',
    title: 'Đơn vị đồng hành',
    subtitle:
      'Cuộc thi vinh dự nhận được sự đồng hành của các đơn vị học thuật, văn phòng luật và tổ chức xã hội.',
    logos: [
      { name: 'UEL · Đại học Kinh tế - Luật' },
      { name: 'ĐHQG TP.HCM' },
      { name: 'Đối tác Học thuật 01' },
      { name: 'Đối tác Học thuật 02' },
      { name: 'Văn phòng Luật 01' },
      { name: 'Văn phòng Luật 02' },
      { name: 'Tổ chức Xã hội 01' },
      { name: 'Nhà tài trợ Chính' },
    ] as PartnerLogo[],
  },

  contact: {
    eyebrow: '[CONTACT // 05]',
    title: 'Liên hệ Ban Tổ chức',
    subtitle: 'Mọi thắc mắc về cuộc thi, vui lòng liên hệ trực tiếp với Ban Tổ chức qua các kênh dưới đây.',
    categories: [
      {
        category: 'Email & Mạng xã hội',
        items: [
          { label: 'Email', value: 'mootcourt@uel.edu.vn' },
          { label: 'Fanpage', value: 'facebook.com/uel.mootcourt' },
          { label: 'Group chính thức', value: 'facebook.com/groups/uel.mootcourt' },
        ],
      },
      {
        category: 'Hotline',
        items: [
          { label: 'Trưởng BTC', value: 'Sẽ được công bố' },
          { label: 'Hỗ trợ thí sinh', value: 'Sẽ được công bố' },
        ],
      },
      {
        category: 'Nền tảng',
        items: [
          { label: 'Website', value: 'mootcourt.n2nai.io' },
          { label: 'Đăng nhập hệ thống', value: '/login' },
        ],
      },
    ] as ContactCategory[],
  },

  footer: {
    organizer: 'Trường Đại học Kinh tế - Luật (UEL) — Đại học Quốc gia TP.HCM',
    contact: 'Liên hệ Ban Tổ chức qua email cuộc thi để biết thêm chi tiết.',
    copyright: 'Gender Justice Moot Court · UEL 2026',
  },
} as const
